import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import ConfirmModal from './ConfirmModal';
import { useApi } from '../hooks/useApi';
import {
    ASSIGNABLE_ROLES,
    ROLE_DESCRIPTIONS,
    roleBadgeClass,
    roleLabel,
} from '../utils/permissions';

// Shimmer sized to the final layout so rows do not jump when data arrives.
const Skel = ({ className = '' }) => (
    <span className={`inline-block bg-gray-200 rounded animate-pulse ${className}`} />
);

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
);

const formatWhen = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
};

const AUDIT_VERBS = {
    create: 'added',
    update: 'changed',
    delete: 'removed',
};

// "changed alice@x.com: admin -> readonly" / "disabled bob@x.com"
const describeAudit = (entry) => {
    const verb = AUDIT_VERBS[entry.action] || entry.action;
    const before = entry.before || {};
    const after = entry.after || {};

    if (entry.action === 'update') {
        const parts = [];
        if (before.role !== after.role) {
            parts.push(`${roleLabel(before.role)} → ${roleLabel(after.role)}`);
        }
        if (before.disabled !== after.disabled) {
            parts.push(after.disabled ? 'disabled' : 're-enabled');
        }
        return parts.length ? `${verb}: ${parts.join(', ')}` : verb;
    }
    if (entry.action === 'create') return `${verb} as ${roleLabel(after.role)}`;
    if (entry.action === 'delete') return `${verb} (was ${roleLabel(before.role)})`;
    return verb;
};

const AdminUsers = ({ googleIdToken, authUser, t }) => {
    const [users, setUsers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuditLoading, setIsAuditLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');
    const [newRole, setNewRole] = useState('readonly');
    const [isAdding, setIsAdding] = useState(false);

    // Which row has an in-flight write, so only that row's controls lock.
    const [busyEmail, setBusyEmail] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [pendingRoleChange, setPendingRoleChange] = useState(null);

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());

    const ownEmail = (authUser?.email || '').toLowerCase();

    const loadUsers = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            setUsers(await apiRequest('/api/admin/users'));
        } catch (error) {
            console.error('Failed to load users:', error);
            setLoadError(error?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }, [apiRequest]);

    const loadAudit = useCallback(async () => {
        setIsAuditLoading(true);
        try {
            setAudit(await apiRequest('/api/admin/users/audit'));
        } catch (error) {
            // The history panel is supporting detail; failing to load it must not
            // take the page down with it.
            console.error('Failed to load user history:', error);
            setAudit([]);
        } finally {
            setIsAuditLoading(false);
        }
    }, [apiRequest]);

    useEffect(() => {
        loadUsers();
        loadAudit();
    }, [loadUsers, loadAudit]);

    const enabledSuperAdmins = useMemo(
        () => users.filter((u) => u.role === 'superadmin' && !u.disabled).length,
        [users]
    );

    // Mirrors the server-side invariants so the UI does not offer an action the
    // backend will refuse. The backend remains the authority — this is courtesy,
    // not enforcement.
    const guardReason = (user, { toRole, toDisabled } = {}) => {
        const isSelf = user.email.toLowerCase() === ownEmail;
        if (isSelf && (toRole !== undefined || toDisabled !== undefined)) {
            return t?.users_cannot_modify_self
                || 'You cannot change your own role or access. Ask another SuperAdmin.';
        }
        const staysEnabledSuper =
            (toRole === undefined ? user.role : toRole) === 'superadmin'
            && !(toDisabled === undefined ? user.disabled : toDisabled);
        const wasEnabledSuper = user.role === 'superadmin' && !user.disabled;
        if (wasEnabledSuper && !staysEnabledSuper && enabledSuperAdmins <= 1) {
            return t?.users_last_superadmin
                || 'This is the last enabled SuperAdmin. Promote someone else first.';
        }
        return null;
    };

    const handleAdd = async (event) => {
        event.preventDefault();
        const email = newEmail.trim().toLowerCase();
        if (!email) return;

        setIsAdding(true);
        try {
            const created = await apiRequest('/api/admin/users', {
                method: 'POST',
                body: JSON.stringify({ email, role: newRole, displayName: newName.trim() }),
            });
            setUsers((prev) => [...prev, created].sort((a, b) => a.email.localeCompare(b.email)));
            setNewEmail('');
            setNewName('');
            toast.success(`${created.email} ${t?.users_added || 'now has access'} (${roleLabel(created.role)}).`);
            loadAudit();
        } catch (error) {
            toast.error(error?.data?.message || error.message || (t?.users_add_failed || 'Could not add that user.'));
        } finally {
            setIsAdding(false);
        }
    };

    const applyUpdate = async (user, patch, successMessage) => {
        setBusyEmail(user.email);
        try {
            const updated = await apiRequest(`/api/admin/users/${encodeURIComponent(user.email)}`, {
                method: 'PATCH',
                body: JSON.stringify(patch),
            });
            setUsers((prev) => prev.map((u) => (u.email === user.email ? { ...u, ...updated } : u)));
            toast.success(successMessage);
            loadAudit();
        } catch (error) {
            toast.error(error?.data?.message || error.message || (t?.users_update_failed || 'Could not update that user.'));
        } finally {
            setBusyEmail(null);
        }
    };

    const handleDelete = async () => {
        const user = pendingDelete;
        if (!user) return;
        setBusyEmail(user.email);
        try {
            await apiRequest(`/api/admin/users/${encodeURIComponent(user.email)}`, { method: 'DELETE' });
            setUsers((prev) => prev.filter((u) => u.email !== user.email));
            toast.success(`${user.email} ${t?.users_removed || 'no longer has access'}.`);
            loadAudit();
        } catch (error) {
            toast.error(error?.data?.message || error.message || (t?.users_delete_failed || 'Could not remove that user.'));
        } finally {
            setBusyEmail(null);
            setPendingDelete(null);
        }
    };

    const handleRoleChange = async () => {
        const change = pendingRoleChange;
        if (!change) return;
        setPendingRoleChange(null);
        await applyUpdate(
            change.user,
            { role: change.role },
            `${change.user.email} ${t?.users_role_now || 'is now'} ${roleLabel(change.role)}.`
        );
    };

    const requestRoleChange = (user, role) => {
        if (role === user.role) return;
        const blocked = guardReason(user, { toRole: role });
        if (blocked) {
            toast.error(blocked);
            return;
        }
        setPendingRoleChange({ user, role });
    };

    const toggleDisabled = (user) => {
        const next = !user.disabled;
        const blocked = guardReason(user, { toDisabled: next });
        if (blocked) {
            toast.error(blocked);
            return;
        }
        applyUpdate(
            user,
            { disabled: next },
            next
                ? `${user.email} ${t?.users_disabled || 'can no longer sign in'}.`
                : `${user.email} ${t?.users_enabled || 'can sign in again'}.`
        );
    };

    const requestDelete = (user) => {
        const blocked = guardReason(user, { toDisabled: true });
        if (blocked) {
            toast.error(blocked);
            return;
        }
        setPendingDelete(user);
    };

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header renders immediately and stays mounted through every state,
                so the page never collapses to a bare loading line. */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {t?.admin_user_management || 'User Management'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                    {t?.users_intro
                        || 'Only people listed here can sign in to this tool. Everyone else is refused, including colleagues on an internal domain.'}
                </p>
            </div>

            {enabledSuperAdmins === 1 && !isLoading && (
                <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                    {t?.users_single_superadmin_warning
                        || 'There is only one enabled SuperAdmin. Nobody can change their own role, so add a second one — otherwise recovering from a mistake means editing Firestore by hand.'}
                </div>
            )}

            {/* --- Add --- */}
            <form onSubmit={handleAdd} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-8">
                <h2 className="font-semibold text-gray-800 mb-3">{t?.users_add_title || 'Give someone access'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        type="email"
                        required
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder={t?.users_email_placeholder || 'name@xebia.com'}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm md:col-span-2"
                    />
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder={t?.users_name_placeholder || 'Name (optional)'}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                        {ASSIGNABLE_ROLES.map((role) => (
                            <option key={role} value={role}>{roleLabel(role)}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-500">{ROLE_DESCRIPTIONS[newRole]}</p>
                    <button
                        type="submit"
                        disabled={isAdding || !newEmail.trim()}
                        className="bg-primary hover:bg-opacity-90 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50 flex items-center gap-2"
                    >
                        {isAdding && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                        {t?.users_add_button || 'Add user'}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    {t?.users_prelist_hint || 'They do not need to have signed in before — you can add them ahead of time.'}
                </p>
            </form>

            {/* --- The list --- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-8">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">{t?.users_col_user || 'User'}</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">{t?.users_col_role || 'Role'}</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">{t?.users_col_last_login || 'Last sign-in'}</th>
                                <th className="px-4 py-3 text-left font-semibold text-gray-600">{t?.users_col_added || 'Added'}</th>
                                <th className="px-4 py-3 text-right font-semibold text-gray-600">{t?.users_col_actions || 'Actions'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading && [...Array(4)].map((_, i) => (
                                <tr key={`skel-${i}`}>
                                    <td className="px-4 py-3"><Skel className="h-4 w-48" /></td>
                                    <td className="px-4 py-3"><Skel className="h-5 w-20 rounded-full" /></td>
                                    <td className="px-4 py-3"><Skel className="h-4 w-32" /></td>
                                    <td className="px-4 py-3"><Skel className="h-4 w-32" /></td>
                                    <td className="px-4 py-3 text-right"><Skel className="h-4 w-24" /></td>
                                </tr>
                            ))}

                            {!isLoading && loadError && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-red-600">
                                        {loadError}
                                        <button onClick={loadUsers} className="ml-3 underline hover:no-underline">
                                            {t?.retry || 'Retry'}
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && !loadError && users.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-gray-400 italic">
                                        {t?.users_empty || 'Nobody has access yet.'}
                                    </td>
                                </tr>
                            )}

                            {!isLoading && !loadError && users.map((user) => {
                                const isSelf = user.email.toLowerCase() === ownEmail;
                                const rowBusy = busyEmail === user.email;
                                return (
                                    <tr key={user.email} className={user.disabled ? 'bg-gray-50' : undefined}>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-800">
                                                {user.displayName || user.email}
                                                {isSelf && (
                                                    <span className="ml-2 text-xs text-gray-500">
                                                        ({t?.users_you || 'you'})
                                                    </span>
                                                )}
                                            </div>
                                            {user.displayName && <div className="text-xs text-gray-500">{user.email}</div>}
                                            {user.disabled && (
                                                <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                                    {t?.users_disabled_pill || 'Access disabled'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={user.role}
                                                disabled={rowBusy || isSelf}
                                                onChange={(e) => requestRoleChange(user, e.target.value)}
                                                title={isSelf
                                                    ? (t?.users_cannot_modify_self || 'You cannot change your own role.')
                                                    : ROLE_DESCRIPTIONS[user.role]}
                                                className={`rounded-full text-xs font-semibold px-2 py-1 disabled:opacity-60 ${roleBadgeClass(user.role)}`}
                                            >
                                                {ASSIGNABLE_ROLES.map((role) => (
                                                    <option key={role} value={role}>{roleLabel(role)}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{formatWhen(user.lastLoginAt)}</td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {formatWhen(user.addedAt)}
                                            {user.addedBy && <div className="text-xs text-gray-400">{t?.users_by || 'by'} {user.addedBy}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <div className="flex justify-end items-center gap-3">
                                                <button
                                                    onClick={() => toggleDisabled(user)}
                                                    disabled={rowBusy || isSelf}
                                                    title={user.disabled ? (t?.users_enable || 'Enable') : (t?.users_disable || 'Disable')}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${user.disabled ? 'bg-gray-300' : 'bg-green-500'} ${rowBusy || isSelf ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user.disabled ? 'translate-x-1' : 'translate-x-6'}`} />
                                                </button>
                                                <button
                                                    onClick={() => requestDelete(user)}
                                                    disabled={rowBusy || isSelf}
                                                    title={t?.delete || 'Delete'}
                                                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- History --- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-800">{t?.users_history_title || 'Recent changes'}</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {t?.users_history_hint || 'Who granted or revoked access, and when.'}
                    </p>
                </div>
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">{t?.users_col_when || 'When'}</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">{t?.users_col_actor || 'By'}</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">{t?.users_col_target || 'User'}</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">{t?.users_col_change || 'Change'}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isAuditLoading && [...Array(3)].map((_, i) => (
                            <tr key={`askel-${i}`}>
                                <td className="px-4 py-2"><Skel className="h-4 w-32" /></td>
                                <td className="px-4 py-2"><Skel className="h-4 w-40" /></td>
                                <td className="px-4 py-2"><Skel className="h-4 w-40" /></td>
                                <td className="px-4 py-2"><Skel className="h-4 w-48" /></td>
                            </tr>
                        ))}
                        {!isAuditLoading && audit.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-gray-400 italic">
                                    {t?.users_history_empty || 'No changes recorded yet.'}
                                </td>
                            </tr>
                        )}
                        {!isAuditLoading && audit.map((entry) => (
                            <tr key={entry.id}>
                                <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatWhen(entry.at)}</td>
                                <td className="px-4 py-2 text-gray-600">{entry.actor}</td>
                                <td className="px-4 py-2 text-gray-800">{entry.target}</td>
                                <td className="px-4 py-2 text-gray-600">{describeAudit(entry)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                isOpen={!!pendingRoleChange}
                title={t?.users_confirm_role_title || 'Change role'}
                message={pendingRoleChange
                    ? `${pendingRoleChange.user.email}\n\n${roleLabel(pendingRoleChange.user.role)} → ${roleLabel(pendingRoleChange.role)}\n\n${ROLE_DESCRIPTIONS[pendingRoleChange.role] || ''}`
                    : ''}
                confirmLabel={t?.users_confirm_role_button || 'Change role'}
                cancelLabel={t?.cancel || 'Cancel'}
                onConfirm={handleRoleChange}
                onCancel={() => setPendingRoleChange(null)}
                busy={!!busyEmail}
                tone="primary"
            />

            <ConfirmModal
                isOpen={!!pendingDelete}
                title={t?.users_confirm_delete_title || 'Remove access'}
                message={pendingDelete
                    ? `${pendingDelete.email}\n\n${t?.users_confirm_delete_message || 'They will be signed out and refused on their next request. Disable instead if you may want to restore access later — that keeps the row.'}`
                    : ''}
                confirmLabel={t?.delete || 'Delete'}
                cancelLabel={t?.cancel || 'Cancel'}
                onConfirm={handleDelete}
                onCancel={() => setPendingDelete(null)}
                busy={!!busyEmail}
                tone="danger"
            />
        </div>
    );
};

export default AdminUsers;
